"use client";

import { useEffect, useRef } from "react";
import * as m from "motion/react-m";
import type { MotionValue } from "motion/react";

import { smoothstep } from "@ui/lib/math";

const DOT_GRID_SPACING = 16;
const DOT_GRID_MOBILE_SPACING = 14;
const DOT_GRID_MOBILE_QUERY = "(max-width: 767px)";
const DOT_GRID_RADIUS = 0.9;
const DOT_GRID_BASE_OPACITY = 0.06;
const DOT_GRID_WAVE_OPACITY = 0.2;
const DOT_GRID_WAVE_RADIUS = 320;
const DOT_GRID_CYCLE_SPEED = 0.00016;
const DOT_GRID_PHASE_WARP = 0.14;
const DOT_GRID_START_JITTER = 0.055;
const DOT_GRID_VISIBLE_PADDING = 0.04;

/**
 * A canvas of faint dots with a soft "wave" of higher opacity that drifts across
 * the grid — the ambient loading surface behind generating states.
 *
 * `scale` lets a caller that zooms its container (e.g. the InteractiveImage
 * create layer) keep the wave inside the visible crop; surfaces without a zoom
 * (e.g. the flow-template placeholder) can omit it and the wave fills the frame.
 */
export function AnimatedDotGrid({ scale, reduceMotion }: { scale?: MotionValue<number>; reduceMotion: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    // Resolve the breakpoint once per effect — `.matches` is still read live in
    // draw(), but constructing the MediaQueryList every frame is needless work.
    const mobileQuery = window.matchMedia(DOT_GRID_MOBILE_QUERY);

    const draw = (time = 0) => {
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      const pixelRatio = window.devicePixelRatio || 1;
      const nextWidth = Math.max(1, Math.round(width * pixelRatio));
      const nextHeight = Math.max(1, Math.round(height * pixelRatio));

      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      }

      const point = reduceMotion ? null : getDotGridPoint(time, width, height, scale?.get() ?? 1);

      context.clearRect(0, 0, width, height);
      context.fillStyle = "black";

      const spacing = mobileQuery.matches ? DOT_GRID_MOBILE_SPACING : DOT_GRID_SPACING;
      const startX = getCenteredDotGridStart(width, spacing);
      const startY = getCenteredDotGridStart(height, spacing);

      for (let y = startY; y < height; y += spacing) {
        for (let x = startX; x < width; x += spacing) {
          const distance = point ? Math.hypot(x - point.x, y - point.y) : DOT_GRID_WAVE_RADIUS;
          const strength = smoothstep(Math.max(0, 1 - distance / DOT_GRID_WAVE_RADIUS));

          context.globalAlpha = DOT_GRID_BASE_OPACITY + strength * DOT_GRID_WAVE_OPACITY;
          context.beginPath();
          context.arc(x, y, DOT_GRID_RADIUS, 0, Math.PI * 2);
          context.fill();
        }
      }

      context.globalAlpha = 1;
    };

    const animate = (time: number) => {
      draw(time);
      frameRef.current = reduceMotion ? null : window.requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(() => draw());
    resizeObserver.observe(canvas);
    draw();

    if (!reduceMotion) {
      frameRef.current = window.requestAnimationFrame(animate);
    }

    return () => {
      resizeObserver.disconnect();
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [reduceMotion, scale]);

  return (
    <m.div className='tw-pointer-events-none tw-absolute tw-inset-0' style={scale ? { scale } : undefined}>
      <canvas ref={canvasRef} className='tw-block tw-size-full' aria-hidden />
    </m.div>
  );
}

function getCenteredDotGridStart(size: number, spacing: number) {
  const center = size / 2;
  const offset = center % spacing;

  return offset === 0 ? spacing : offset;
}

function getDotGridPoint(time: number, width: number, height: number, scale: number) {
  const phase = getDotGridPhase(time);
  const visibleInset = Math.max(0, (1 - 1 / Math.max(1, scale)) / 2);
  const visibleRadius = Math.max(0, 0.5 - visibleInset - DOT_GRID_VISIBLE_PADDING);
  const xRadius = Math.min(0.46, visibleRadius);
  const yRadius = Math.min(0.4, visibleRadius);

  return {
    x: width * (0.5 + Math.cos(phase) * xRadius),
    y: height * (0.5 + Math.sin(phase * 0.73) * yRadius),
  };
}

function getDotGridPhase(time: number) {
  const progress = (time * DOT_GRID_CYCLE_SPEED) % 1;
  const startWeight = Math.max(0, 1 - progress / 0.28) ** 2;
  const startJitter =
    (Math.sin(progress * Math.PI * 18) + Math.sin(progress * Math.PI * 47 + 0.8) * 0.5) *
    DOT_GRID_START_JITTER *
    startWeight;
  const warpedProgress = progress + Math.sin(progress * Math.PI * 2) * DOT_GRID_PHASE_WARP + startJitter;

  return warpedProgress * Math.PI * 2;
}
